import {Component, Input} from '@angular/core';
import {Store} from '@ngrx/store';
import {take} from 'rxjs/operators';
import {DateUtilsService} from '../../../../shared/shared-utils/date/dateUtilsService.service';
import {
  IAutoOrderNotificationItemErrorEntry,
  IAutoOrderNotificationItemErrorGuidance,
  INotification,
} from '../../models';
import {DEFAULT_LOADER_INFINITE} from '../../../../shared/shared-module-ui/models/animations/loading-g.interface';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {
  AutoOrderFailureType,
  AutoOrderStatusService,
} from '../../../auto-ordering/service/auto-order-status.service';
import {setRetryAutoOrder} from '../../../auto-ordering/store/auto-ordering.actions';
import {wouldPartialRetryEmptyOrder} from '../../../auto-ordering/utils/auto-order-partial-retry.utils';
import {IAutoOrderOrder} from '../../../auto-ordering/models';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IStore} from '../../../../shared/shared-models/store/store';
import {IUser} from '../../../user-access/models';
import {setNavigation, setSelectedStore} from '../../../user-access/user-stores-store/user-store.actions';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {NavigationPageEnum, NavigationTabEnum} from '../../../user-access/models/navigations.enums';
import {setGlobalModalVisibility} from '../../../user-access/user-store/user.actions';
import {requestOpenAutoOrderFromNotification} from '../../../auto-ordering/store/auto-ordering.actions';
import {updateNotification} from '../../store/notification.actions';
import {
  buildAutoOrderNotificationItemErrorEntries,
  buildAutoOrderNotificationItemErrorGuidance,
  resolveAutoOrderNotificationItemErrors,
} from '../../utils/notification-auto-order-error.utils';

@Component({
  selector: 'app-notification-type-auto-order',
  templateUrl: './notification-type-auto-order.component.html',
  styleUrls: ['./notification-type-auto-order.component.scss'],
  standalone: false,
})
export class NotificationTypeAutoOrderComponent {

  isAutoOrderLoading = false;

  #autoOrderLoading: Record<string, boolean> = {};

  protected readonly icons = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly animation = DEFAULT_LOADER_INFINITE;

  #autoOrder: IAutoOrderOrder;
  #notification: INotification;
  #stores: IStore[] = [];
  #user: IUser;

  constructor(
    private readonly dateUtilsService: DateUtilsService,
    private readonly autoOrderStatusService: AutoOrderStatusService,
    private readonly store: Store,
  ) {
  }

  get getAutoOrder(): IAutoOrderOrder {
    return this.#autoOrder;
  }

  get getGeneratedDate(): string {
    return this.dateUtilsService.formatPropertyDate<IAutoOrderOrder>(
      'MMM dd, yyyy',
      this.getAutoOrder,
      'generated',
      true,
    );
  }

  get getIsComponentLoading(): boolean {
    return this.isAutoOrderLoading === false && !!this.getNotification;
  }

  get getNotification(): INotification {
    return this.#notification;
  }

  get getNotificationOrderId(): string {
    return this.#notification?.event;
  }

  get getPurchaseOrderNo(): string {
    return this.getAutoOrder?.purchaseOrderNo ? this.#autoOrder.purchaseOrderNo.toString() : 'No P/o Found';
  }

  get getSupplierName(): string {
    return this.getAutoOrder?.supplierName || this.getAutoOrder?.supplierId;
  }

  get showErrorPanel(): boolean {
    return this.getNotification?.success !== true;
  }

  get hasFailureContent(): boolean {
    return !!this.getNotification?.response || this.failingItems.length > 0;
  }

  get failingItems(): IAutoOrderNotificationItemErrorEntry[] {
    return buildAutoOrderNotificationItemErrorEntries(
      resolveAutoOrderNotificationItemErrors(this.getNotification, this.getAutoOrder),
    );
  }

  get itemErrorGuidance(): IAutoOrderNotificationItemErrorGuidance[] {
    return buildAutoOrderNotificationItemErrorGuidance(this.failingItems);
  }

  get canRetryOrder(): boolean {
    const order = this.getAutoOrder;
    if (!order || this.getNotification?.retried) {
      return false;
    }
    return order.iqStatus === 'FAILED' || order.status === 'FAILED';
  }

  get canRetryWithoutFailedItems(): boolean {
    const failingItems: IAutoOrderNotificationItemErrorEntry[] = this.failingItems;
    if (failingItems.length === 0 || !this.getAutoOrder || this.getNotification?.retried) {
      return false;
    }

    const codesToRemove: string[] = failingItems.map(
      (item: IAutoOrderNotificationItemErrorEntry) => item.stockCode,
    );

    return !wouldPartialRetryEmptyOrder(this.getAutoOrder, codesToRemove);
  }

  @Input() set setAutoOrders(autoOrders: IAutoOrderOrder[]) {
    if (autoOrders?.length > 0 && this.getNotificationOrderId) {
      const foundOrder: IAutoOrderOrder | undefined = autoOrders.find(
        (order: IAutoOrderOrder): boolean =>
          order.orderId === this.getNotificationOrderId || order.orderId === this.#notification?.event,
      );
      if (foundOrder) {
        this.#autoOrder = {
          ...foundOrder,
          orderId: foundOrder.orderId ?? this.getNotificationOrderId,
        };
      }
    }
  }

  @Input() set setUser(user: IUser) {
    this.#user = user;
  }

  @Input() set setUserStores(stores: IStore[]) {
    this.#stores = stores ?? [];
  }

  @Input() set setIsAutoOrderLoading(isLoading: Record<string, boolean>) {
    this.#autoOrderLoading = isLoading ?? {};
    this.applyAutoOrderLoading();
  }

  @Input() set setNotification(notify: INotification) {
    this.#notification = notify;
    this.applyAutoOrderLoading();
  }

  /**
   * Shows the loader only while this notification's order fetch is in flight.
   * A missing flag means the fetch is not running, so the detail can render.
   */
  private applyAutoOrderLoading(): void {
    const orderId: string = this.getNotificationOrderId;
    if (!orderId) {
      this.isAutoOrderLoading = false;
      return;
    }
    this.isAutoOrderLoading = this.#autoOrderLoading[orderId] === true;
  }

  getStatusIcon(
    order: IAutoOrderOrder,
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
  ): IconsEnum {
    return this.autoOrderStatusService.getStatusIcon(order, statusProperty);
  }

  getStatusIconColour(
    order: IAutoOrderOrder,
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
  ): IconColourEnum {
    return this.autoOrderStatusService.getStatusColour(order, statusProperty);
  }

  getStatusTooltip(
    order: IAutoOrderOrder,
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
  ): string {
    return this.autoOrderStatusService.getStatusToolTip(order, statusProperty);
  }

  onStatusIconClick(
    order: IAutoOrderOrder,
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
  ): void {
    const status = order[statusProperty];
    if (status === 'FAILED') {
      const failureType: AutoOrderFailureType = this.mapStatusPropertyToFailureType(statusProperty);
      if (failureType) {
        void this.autoOrderStatusService.showOrderFailureModal(order, failureType, {
          user: this.#user,
          notification: this.getNotification,
          storeId: this.getNotification?.storeId,
          viewOnly: true,
        });
      }
      return;
    }
    this.autoOrderStatusService.onNotificationStatusIconClick(order, statusProperty, {
      user: this.#user,
      notification: this.getNotification,
      storeId: this.getNotification?.storeId,
    });
  }

  /**
   * Leaves the notification panel and opens this order on Auto Ordering.
   */
  onOpenRelatedOrder(): void {
    const orderId: string = this.getAutoOrder?.orderId || this.getNotificationOrderId;
    const storeId: string = this.getNotification?.storeId;
    if (!orderId || !storeId) {
      return;
    }
    this.store.select(selectSelectedStore).pipe(take(1)).subscribe((selectedStore: IStore | undefined) => {
      if (selectedStore?.storeId !== storeId) {
        const nextStore: IStore | undefined = this.#stores.find((store: IStore) => store.storeId === storeId);
        if (nextStore) {
          this.store.dispatch(setSelectedStore({selectedStore: nextStore}));
        }
      }
      this.store.dispatch(requestOpenAutoOrderFromNotification({
        orderId,
        storeId,
        order: this.getAutoOrder ?? null,
      }));
      this.store.dispatch(setNavigation({
        feature: NavigationPageEnum.AutoOrdering,
        navigation: {
          currentPage: NavigationPageEnum.AutoOrdering,
          currentTab: NavigationTabEnum.Orders,
        },
      }));
      this.store.dispatch(setGlobalModalVisibility({visibility: false, modalType: []}));
    });
  }

  onViewErrorClick(): void {
    void this.autoOrderStatusService.showOrderErrorViewModal(this.getAutoOrder, this.getNotification);
  }

  onRetryOrderClick(): void {
    if (!this.canRetryOrder) {
      return;
    }
    const order: IAutoOrderOrder = this.getAutoOrder;
    const notification: INotification = this.getNotification;
    const selectedStore: IStore | undefined = this.#stores.find(
      (store: IStore) => store.storeId === notification.storeId,
    );
    if (selectedStore) {
      this.store.dispatch(setSelectedStore({selectedStore}));
    }
    if (!notification.retried) {
      const updatedNotification: INotification = {...notification, retried: true};
      this.#notification = updatedNotification;
      this.store.dispatch(updateNotification({notification: updatedNotification}));
    }
    this.store.dispatch(setRetryAutoOrder({order}));
  }

  onRetryWithoutFailedItemsClick(): void {
    if (!this.canRetryWithoutFailedItems) {
      return;
    }

    const order: IAutoOrderOrder = this.getAutoOrder;
    const selectedStore: IStore | undefined = this.#stores.find(
      (store: IStore) => store.storeId === this.getNotification.storeId,
    );

    if (selectedStore) {
      this.store.dispatch(setSelectedStore({selectedStore}));
    }

    const codesToRemove: string[] = this.failingItems.map(
      (item: IAutoOrderNotificationItemErrorEntry) => item.stockCode,
    );

    void this.autoOrderStatusService.confirmAndRetryOrderWithoutFailedItems(
      order,
      {
        user: this.#user,
        notification: this.getNotification,
        storeId: this.getNotification.storeId,
      },
      codesToRemove,
      this.failingItems,
    );
  }

  private mapStatusPropertyToFailureType(
    statusProperty: keyof Pick<IAutoOrderOrder, 'emailStatus' | 'iqStatus' | 'pdfStatus' | 'status'>,
  ): AutoOrderFailureType | null {
    switch (statusProperty) {
      case 'emailStatus':
        return 'EMAIL';
      case 'pdfStatus':
        return 'PDF';
      case 'iqStatus':
        return 'IQ';
      case 'status':
        return 'STATUS';
      default:
        return null;
    }
  }

}
